import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, User, Heart, Search, Menu, X, ChevronDown, ChevronRight, Loader2, Gift, Target } from 'lucide-react';
import { Input } from './ui/input';
import Logo from './Logo';
import { useAuth } from '../context/AuthContext';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import CurrencySelector from './CurrencySelector';
import api from '../services/api';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [categories, setCategories] = useState([]);
  const [menuLoading, setMenuLoading] = useState(true);
  const [concernsByCategory, setConcernsByCategory] = useState({});
  const searchRef = useRef(null);
  const menuTimeoutRef = useRef(null);
  const navigate = useNavigate();
  
  const { user, isAuthenticated } = useAuth();
  const { wishlistCount } = useWishlist();
  const { cartCount } = useCart();

  // Fetch categories (single source of truth for menu)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await api.getCategories(false);
        if (data.categories && data.categories.length > 0) {
          setCategories(data.categories);
        }
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      } finally {
        setMenuLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Fetch concerns for all categories
  useEffect(() => {
    const fetchConcerns = async () => {
      try {
        const data = await api.getConcerns(false);
        // Group concerns by category_id
        const grouped = {};
        (data.concerns || []).forEach(concern => {
          const catId = concern.category_id;
          if (catId) {
            if (!grouped[catId]) grouped[catId] = [];
            grouped[catId].push(concern);
          }
        });
        setConcernsByCategory(grouped);
      } catch (error) {
        console.error('Failed to fetch concerns:', error);
      }
    };
    fetchConcerns();
  }, []);

  // Debounce search suggestions
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoadingSuggestions(true);
      try {
        const results = await api.getSearchSuggestions(searchQuery);
        setSuggestions(results);
      } catch (error) {
        console.error('Failed to fetch suggestions:', error);
        setSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Close suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (menuTimeoutRef.current) {
        clearTimeout(menuTimeoutRef.current);
      }
    };
  }, []);

  const handleMenuEnter = (categoryId) => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
    }
    setActiveMenu(categoryId);
  };

  const handleMenuLeave = () => {
    menuTimeoutRef.current = setTimeout(() => {
      setActiveMenu(null);
    }, 150); // Small delay to allow mouse to reach dropdown
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (suggestion.type === 'product' && suggestion.id) {
      navigate(`/product/${suggestion.id}`);
    } else if (suggestion.type === 'category') {
      navigate(`/shop/${suggestion.text.toLowerCase().replace(/\s+/g, '-')}`);
    } else if (suggestion.type === 'brand') {
      navigate(`/search?q=${encodeURIComponent(suggestion.text)}`);
    }
    setSearchQuery('');
    setShowSuggestions(false);
  };

  return (
    <header className="bg-brand-cream border-b border-brand-border sticky top-0 z-50" role="banner">
      {/* Skip to main content link for accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-brand-gold focus:text-white"
      >
        Skip to main content
      </a>
      
      {/* Top bar - Luxury subtle */}
      <div className="bg-brand-charcoal text-white text-[11px] py-2.5">
        <div className="container mx-auto px-4 text-center tracking-wider">
          Free UK delivery on orders over £30 | Use code <span className="text-brand-gold font-medium">WELCOME20</span> for 20% off your first order
        </div>
      </div>

      {/* Main header */}
      <div className="border-b border-brand-border">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20 py-2">
            {/* Logo - Left aligned */}
            <Logo linkTo="/" />

            {/* Right side - Search and Icons */}
            <div className="flex items-center space-x-4">
              {/* Currency Selector */}
              <div className="hidden md:block">
                <CurrencySelector />
              </div>

              {/* Search with Autocomplete */}
              <form onSubmit={handleSearch} className="hidden lg:flex items-center" ref={searchRef} role="search" aria-label="Search products">
                <div className="relative w-64">
                  <label htmlFor="search-input" className="sr-only">Search products</label>
                  <Input
                    id="search-input"
                    type="search"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowSuggestions(true);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    className="w-full pl-10 pr-8"
                    data-testid="search-input"
                    aria-autocomplete="list"
                    aria-controls="search-suggestions"
                    aria-expanded={showSuggestions && suggestions.length > 0}
                  />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} aria-hidden="true" />
                  {loadingSuggestions && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" size={16} aria-hidden="true" />
                  )}

                  {/* Search Suggestions Dropdown */}
                  {showSuggestions && suggestions.length > 0 && (
                    <ul 
                      id="search-suggestions"
                      className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto z-50" 
                      data-testid="search-suggestions"
                      role="listbox"
                      aria-label="Search suggestions"
                    >
                      {suggestions.map((suggestion, index) => (
                        <li key={index} role="option">
                          <button
                            type="button"
                            onClick={() => handleSuggestionClick(suggestion)}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between border-b border-gray-100 last:border-0 focus:bg-gray-50 focus:outline-none"
                            data-testid={`suggestion-${index}`}
                          >
                            <span className="text-sm">{suggestion.text}</span>
                            <span className="text-xs text-gray-400 uppercase">{suggestion.type}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </form>

              {/* User Icon */}
              <Link 
                to={isAuthenticated ? "/account" : "/login"} 
                className="p-2 hover:bg-brand-cream rounded-full transition-colors relative group focus:outline-none focus:ring-2 focus:ring-brand-gold focus:ring-offset-2"
                data-testid="user-icon"
                aria-label={isAuthenticated ? `Account - ${user?.first_name}` : 'Sign in to your account'}
              >
                <User size={20} className="text-gray-700" aria-hidden="true" />
                {isAuthenticated && (
                  <span className="absolute -bottom-1 -right-1 w-2 h-2 bg-brand-gold rounded-full" aria-hidden="true"></span>
                )}
                {/* Tooltip */}
                <span className="absolute top-full mt-1 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none" aria-hidden="true">
                  {isAuthenticated ? user?.first_name : 'Sign In'}
                </span>
              </Link>

              {/* Wishlist Icon */}
              <Link 
                to="/wishlist" 
                className="p-2 hover:bg-brand-cream rounded-full transition-colors relative focus:outline-none focus:ring-2 focus:ring-brand-gold focus:ring-offset-2"
                data-testid="wishlist-icon"
                aria-label={`Wishlist${wishlistCount > 0 ? ` - ${wishlistCount} items` : ''}`}
              >
                <Heart size={20} className="text-gray-700" aria-hidden="true" />
                {wishlistCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-brand-gold text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium" data-testid="wishlist-count" aria-hidden="true">
                    {wishlistCount}
                  </span>
                )}
              </Link>

              {/* Cart Icon */}
              <Link 
                to="/cart" 
                className="p-2 hover:bg-brand-cream rounded-full transition-colors relative focus:outline-none focus:ring-2 focus:ring-brand-gold focus:ring-offset-2"
                data-testid="cart-icon"
                aria-label={`Shopping bag${cartCount > 0 ? ` - ${cartCount} items` : ''}`}
              >
                <ShoppingBag size={20} className="text-gray-700" aria-hidden="true" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-brand-gold text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-medium" aria-hidden="true">
                    {cartCount}
                  </span>
                )}
              </Link>
              
              {/* Mobile menu button */}
              <button
                className="lg:hidden p-2 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:ring-offset-2 rounded hover:bg-brand-cream transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-menu"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
              </button>
            </div>
          </div>

          {/* Mobile Search */}
          <form onSubmit={handleSearch} className="lg:hidden pb-4" role="search" aria-label="Search products mobile">
            <div className="relative">
              <label htmlFor="mobile-search-input" className="sr-only">Search products</label>
              <Input
                id="mobile-search-input"
                type="search"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} aria-hidden="true" />
            </div>
          </form>
        </div>
      </div>

      {/* Full-Width Mega Menu Navigation */}
      <nav className="hidden lg:block bg-white border-b border-gray-50" aria-label="Main navigation">
        <div className="container mx-auto px-4">
          <ul className="flex items-center justify-center space-x-8 h-12">
            {/* Collections Link */}
            <li className="h-full flex items-center">
              <Link 
                to="/collections" 
                className="text-sm font-medium hover:text-brand-gold transition-colors h-full flex items-center px-2"
                data-testid="nav-collections"
              >
                Collections
              </Link>
            </li>
            
            {/* Bundles Link */}
            <li className="h-full flex items-center">
              <Link 
                to="/bundles" 
                className="text-sm font-medium hover:text-brand-gold transition-colors h-full flex items-center px-2 text-brand-gold"
              >
                <Gift size={16} className="mr-1.5" aria-hidden="true" />
                Bundles & Sets
              </Link>
            </li>
            
            {categories.map((category) => {
              // Split subcategories into columns (max 8 items per column)
              const subcats = category.subcategories || [];
              const columns = [];
              const itemsPerColumn = 8;
              
              for (let i = 0; i < subcats.length; i += itemsPerColumn) {
                columns.push(subcats.slice(i, i + itemsPerColumn));
              }
              
              // If no subcategories, create a "View All" column
              if (columns.length === 0) {
                columns.push([]);
              }
              
              // Get concerns for this category
              const categoryConcerns = concernsByCategory[category.id] || [];
              
              return (
                <li
                  key={category.id}
                  className="relative h-full flex items-center"
                  onMouseEnter={() => handleMenuEnter(category.id)}
                  onMouseLeave={handleMenuLeave}
                >
                  <button className="flex items-center space-x-1 text-sm font-medium hover:text-brand-gold transition-colors h-full px-2" aria-expanded={activeMenu === category.id} aria-haspopup="true">
                    <span>{category.name}</span>
                    <ChevronDown size={14} className={`transition-transform ${activeMenu === category.id ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </button>

                  {/* Mega Menu Dropdown - Full width below nav */}
                  {activeMenu === category.id && (
                    <div 
                      className="absolute left-1/2 -translate-x-1/2 top-full pt-2"
                      onMouseEnter={() => handleMenuEnter(category.id)}
                      onMouseLeave={handleMenuLeave}
                    >
                      {/* Invisible bridge to prevent gap */}
                      <div className="absolute -top-2 left-0 right-0 h-2" />
                      
                      <div className="bg-white shadow-xl border border-gray-100 rounded-xl min-w-[500px] max-w-[750px]">
                        <div className="p-6">
                          <div className={`grid gap-6 ${categoryConcerns.length > 0 ? 'grid-cols-3' : columns.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                            {/* Shop by Category - Subcategories columns */}
                            {columns.map((columnSubcats, colIndex) => (
                              <div key={colIndex}>
                                <h3 className="text-xs font-bold mb-3 uppercase tracking-wide text-brand-gold-dark border-b border-brand-cream-dark pb-2">
                                  {colIndex === 0 ? 'Shop by Category' : 'More Categories'}
                                </h3>
                                <ul className="space-y-1">
                                  {/* View All link at top */}
                                  {colIndex === 0 && (
                                    <li>
                                      <Link
                                        to={`/shop/${category.slug}`}
                                        className="text-sm text-gray-600 hover:text-brand-gold transition-colors block py-1.5 hover:pl-1 transition-all font-medium"
                                        onClick={() => setActiveMenu(null)}
                                      >
                                        View All {category.name}
                                      </Link>
                                    </li>
                                  )}
                                  {columnSubcats.map((subcat) => (
                                    <li key={subcat.id}>
                                      <Link
                                        to={`/shop/${category.slug}/${subcat.slug}`}
                                        className="text-sm text-gray-600 hover:text-brand-gold transition-colors block py-1.5 hover:pl-1 transition-all"
                                        onClick={() => setActiveMenu(null)}
                                      >
                                        {subcat.name}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                            
                            {/* Shop by Concern column */}
                            {categoryConcerns.length > 0 && (
                              <div>
                                <h3 className="text-xs font-bold mb-3 uppercase tracking-wide text-brand-gold-dark border-b border-brand-cream-dark pb-2 flex items-center gap-1">
                                  <Target size={12} aria-hidden="true" />
                                  Shop by Concern
                                </h3>
                                <ul className="space-y-1">
                                  {categoryConcerns.slice(0, 8).map((concern) => (
                                    <li key={concern.id}>
                                      <Link
                                        to={`/shop/concern/${concern.slug}`}
                                        className="text-sm text-gray-600 hover:text-brand-gold transition-colors block py-1.5 hover:pl-1 transition-all"
                                        onClick={() => setActiveMenu(null)}
                                      >
                                        {concern.name}
                                      </Link>
                                    </li>
                                  ))}
                                  {categoryConcerns.length > 8 && (
                                    <li>
                                      <Link
                                        to="/shop/concern"
                                        className="text-sm text-brand-gold hover:text-brand-gold-dark font-medium block py-1.5"
                                        onClick={() => setActiveMenu(null)}
                                      >
                                        View All →
                                      </Link>
                                    </li>
                                  )}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 max-h-[80vh] overflow-y-auto">
          <div className="container mx-auto px-4 py-4">
            {/* Auth links for mobile */}
            <div className="border-b border-gray-200 pb-4 mb-4">
              {isAuthenticated ? (
                <Link 
                  to="/account" 
                  className="flex items-center space-x-3 p-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <div className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center font-bold">
                    {user?.first_name?.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium">{user?.first_name} {user?.last_name}</p>
                    <p className="text-sm text-gray-500">View Account</p>
                  </div>
                </Link>
              ) : (
                <div className="flex space-x-3">
                  <Link 
                    to="/login"
                    className="flex-1 text-center py-2 border border-black rounded font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign In
                  </Link>
                  <Link 
                    to="/register"
                    className="flex-1 text-center py-2 bg-black text-white rounded font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Register
                  </Link>
                </div>
              )}
            </div>

            <nav className="space-y-2">
              {/* Collections Link for Mobile */}
              <Link 
                to="/collections" 
                className="block font-medium py-2 text-brand-gold border-b border-gray-100"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-nav-collections"
              >
                Collections
              </Link>
              
              {categories.map((category) => (
                <div key={category.id} className="border-b border-gray-100 pb-2">
                  <button
                    onClick={() => setActiveMenu(activeMenu === category.id ? null : category.id)}
                    className="flex items-center justify-between w-full font-medium text-left py-2"
                  >
                    <span>{category.name}</span>
                    <ChevronRight 
                      size={16} 
                      className={`transition-transform ${activeMenu === category.id ? 'rotate-90' : ''}`}
                      aria-hidden="true"
                    />
                  </button>
                  
                  {activeMenu === category.id && (
                    <div className="mt-2 ml-4 space-y-3">
                      {/* View All link */}
                      <Link
                        to={`/shop/${category.slug}`}
                        className="text-sm text-gray-700 block py-1 font-medium"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        View All {category.name}
                      </Link>
                      
                      {/* Subcategories */}
                      {category.subcategories && category.subcategories.length > 0 && (
                        <div>
                          <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">
                            Categories
                          </h4>
                          <ul className="space-y-1">
                            {category.subcategories.map((subcat) => (
                              <li key={subcat.id}>
                                <Link
                                  to={`/shop/${category.slug}/${subcat.slug}`}
                                  className="text-sm text-gray-700 block py-1"
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  {subcat.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Concerns */}
                      {concernsByCategory[category.id]?.length > 0 && (
                        <div>
                          <h4 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">
                            Shop by Concern
                          </h4>
                          <ul className="space-y-1">
                            {concernsByCategory[category.id].slice(0, 6).map((concern) => (
                              <li key={concern.id}>
                                <Link
                                  to={`/shop/concern/${concern.slug}`}
                                  className="text-sm text-gray-700 block py-1"
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  {concern.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
